import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Trash2, Pencil, Clock } from "lucide-react";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";

export default function Services() {
  const { apiCall } = useAuth();
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", description: "", price: "", cost: "", duration_minutes: "", commission_rate: "0" });

  const load = async () => {
    setLoading(true);
    try {
      const res = await apiCall("get", "/services");
      setServices(res.data);
    } catch (e) {
      toast.error("Erro ao carregar serviços");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleOpenNew = () => {
    setEditing(null);
    setForm({ name: "", description: "", price: "", cost: "", duration_minutes: "", commission_rate: "0" });
    setOpen(true);
  };

  const handleEdit = (s) => {
    setEditing(s);
    setForm({
      name: s.name,
      description: s.description || "",
      price: s.price.toString(),
      cost: (s.cost || 0).toString(),
      duration_minutes: s.duration_minutes.toString(),
      commission_rate: (s.commission_rate || 0).toString(),
    });
    setOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...form,
        price: parseFloat(form.price),
        cost: parseFloat(form.cost) || 0,
        duration_minutes: parseInt(form.duration_minutes),
        commission_rate: parseFloat(form.commission_rate) || 0,
      };
      if (editing) {
        await apiCall("put", `/services/${editing.service_id}`, payload);
        toast.success("Serviço atualizado");
      } else {
        await apiCall("post", "/services", payload);
        toast.success("Serviço criado");
      }
      setOpen(false);
      load();
    } catch (err) {
      toast.error("Erro ao salvar");
    }
  };

  const handleDelete = async (id) => {
    if (!confirm("Excluir este serviço?")) return;
    try {
      await apiCall("delete", `/services/${id}`);
      toast.success("Serviço excluído");
      load();
    } catch {
      toast.error("Erro ao excluir");
    }
  };

  const formatCurrency = (value) =>
    new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(value);

  return (
    <div className="space-y-8" data-testid="services-page">
      <PageHeader overline="Cadastros" title="Serviços" subtitle="Gerencie os serviços do seu salão">
        <Button onClick={handleOpenNew} data-testid="add-service-button">
          <Plus className="h-4 w-4 mr-2" strokeWidth={1.5} />
          Novo Serviço
        </Button>
      </PageHeader>

      <Card className="border border-border bg-card shadow-soft rounded-md overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-secondary/30 hover:bg-secondary/30">
              <TableHead className="text-xs uppercase tracking-widest font-medium">Nome</TableHead>
              <TableHead className="text-xs uppercase tracking-widest font-medium">Descrição</TableHead>
              <TableHead className="text-xs uppercase tracking-widest font-medium">Duração</TableHead>
              <TableHead className="text-xs uppercase tracking-widest font-medium">Preço</TableHead>
              <TableHead className="text-xs uppercase tracking-widest font-medium">Custo</TableHead>
              <TableHead className="text-xs uppercase tracking-widest font-medium">Margem</TableHead>
              <TableHead className="text-xs uppercase tracking-widest font-medium">Comissão</TableHead>
              <TableHead className="text-right text-xs uppercase tracking-widest font-medium">Ações</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={6} className="text-center py-8 text-muted-foreground">Carregando...</TableCell></TableRow>
            ) : services.length === 0 ? (
              <TableRow><TableCell colSpan={6} className="text-center py-12 text-muted-foreground">Nenhum serviço cadastrado.</TableCell></TableRow>
            ) : (
              services.map((s) => (
                <TableRow key={s.service_id} data-testid={`service-row-${s.service_id}`}>
                  <TableCell className="font-medium">{s.name}</TableCell>
                  <TableCell className="text-sm text-muted-foreground max-w-xs truncate">{s.description || "—"}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2 text-sm">
                      <Clock className="h-3 w-3 text-muted-foreground" strokeWidth={1.5} />
                      {s.duration_minutes} min
                    </div>
                  </TableCell>
                  <TableCell className="font-medium" style={{ color: "#CBA871" }}>{formatCurrency(s.price)}</TableCell>
                  <TableCell className="text-sm text-muted-foreground">{formatCurrency(s.cost || 0)}</TableCell>
                  <TableCell className="text-sm font-medium" style={{ color: (s.price - (s.cost || 0)) >= 0 ? "#166534" : "#991B1B" }}>
                    {formatCurrency((s.price || 0) - (s.cost || 0))}
                  </TableCell>
                  <TableCell className="text-sm">{s.commission_rate || 0}%</TableCell>
                  <TableCell className="text-right">
                    <Button variant="ghost" size="icon" onClick={() => handleEdit(s)} data-testid={`edit-service-${s.service_id}`}>
                      <Pencil className="h-4 w-4" strokeWidth={1.5} />
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => handleDelete(s.service_id)} data-testid={`delete-service-${s.service_id}`}>
                      <Trash2 className="h-4 w-4 text-destructive" strokeWidth={1.5} />
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-light tracking-tight text-2xl">{editing ? "Editar Serviço" : "Novo Serviço"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4 mt-4">
            <div>
              <Label className="text-xs uppercase tracking-widest">Nome</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="service-name-input" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest">Descrição</Label>
              <Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="service-description-input" />
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label className="text-xs uppercase tracking-widest">Preço (R$)</Label>
                <Input type="number" step="0.01" min="0" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} required data-testid="service-price-input" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest">Custo (R$)</Label>
                <Input type="number" step="0.01" min="0" value={form.cost} onChange={(e) => setForm({ ...form, cost: e.target.value })} placeholder="0,00" data-testid="service-cost-input" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest">Duração (min)</Label>
                <Input type="number" min="1" value={form.duration_minutes} onChange={(e) => setForm({ ...form, duration_minutes: e.target.value })} required data-testid="service-duration-input" />
              </div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest">Comissão do Serviço (%)</Label>
              <Input
                type="number"
                step="0.1"
                min="0"
                max="100"
                value={form.commission_rate}
                onChange={(e) => setForm({ ...form, commission_rate: e.target.value })}
                placeholder="0"
                data-testid="service-commission-input"
              />
              <p className="text-xs text-muted-foreground mt-1">Sobrepõe a comissão padrão do funcionário. Deixe 0 para usar a do funcionário.</p>
            </div>
            <DialogFooter className="gap-2">
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
              <Button type="submit" data-testid="service-save-button">{editing ? "Atualizar" : "Criar"}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
